import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    withStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelHttpReq } from '../../utils/axiosInstance';
import { endpoint } from '../../utils/constants';
import DisplayError from '../DisplayError/DisplayError';
import StatisticsChart from './StatisticsChart';

// How many waterpipe rows to show in the dropdown before scrolling.
// Set to 5 for testing, change to 8 for production.
const WATERPIPE_DROPDOWN_MAX_VISIBLE = 8;
const WATERPIPE_DROPDOWN_ROW_HEIGHT = 33;

const ITEM_COLORS = {
    total: '#3f51b5',
    tcp: '#2196f3',
    smsc: '#4caf50',
    sms: '#ff9800',
    v7: '#e91e63',
    v8: '#9c27b0',
    v9: '#673ab7',
    v10: '#00bcd4',
    v11: '#009688',
    v12: '#8bc34a',
    duplicate: '#795548',
    parser_error: '#f44336',
    date_error: '#9e9e9e',
};

const ITEM_LABELS = {
    total: 'Liczba wszystkich pakietów',
    tcp: 'TCP',
    smsc: 'SMSC',
    sms: 'SMS',
    v7: 'V7',
    v8: 'V8',
    v9: 'V9',
    v10: 'V10',
    v11: 'V11',
    v12: 'V12',
    duplicate: 'Duplikaty',
    parser_error: 'Błąd parsowania',
    date_error: 'Błąd daty',
};

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        paddingBottom: '3rem',
    },
    filters: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '2rem',
        padding: '1rem',
        width: '100%',
        maxWidth: 900,
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
    },
    filterLabel: {
        fontSize: '0.8rem',
        color: '#595652',
    },
    filterInput: {
        padding: '0.5rem',
        border: '1px solid #babfc7',
        borderRadius: 4,
        fontSize: '0.9rem',
        minWidth: 140,
        cursor: 'text',
    },
    filterSelect: {
        padding: '0.5rem',
        border: '1px solid #babfc7',
        borderRadius: 4,
        fontSize: '0.9rem',
        minWidth: 180,
        background: '#ffffff',
    },
    chartsGrid: {
        display: 'grid',
        gap: '1.5rem',
        width: '100%',
        maxWidth: 1400,
    },
    chartsGridSingle: {
        gridTemplateColumns: '1fr',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
        },
    },
    chartsGridDouble: {
        gridTemplateColumns: '1fr 1fr',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
        },
    },
    chartWrapper: {
        background: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        padding: '1rem',
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem',
    },
    chartTitle: {
        color: '#595652',
        fontSize: '1.2rem',
        margin: 0,
    },
    editButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    checkboxPanelWrapper: {
        position: 'relative',
    },
    checkboxTrigger: {
        padding: '0.5rem',
        border: '1px solid #babfc7',
        borderRadius: 4,
        fontSize: '0.9rem',
        minWidth: 180,
        background: '#ffffff',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 37,
        boxSizing: 'border-box',
    },
    checkboxPanel: {
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 10,
        minWidth: 220,
        backgroundColor: '#ffffff',
        border: '1px solid #babfc7',
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        marginTop: 4,
        maxHeight: WATERPIPE_DROPDOWN_ROW_HEIGHT * (WATERPIPE_DROPDOWN_MAX_VISIBLE + 1),
        overflowY: 'auto',
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.4rem 0.75rem',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
    },
    checkboxLabel: {
        marginLeft: 8,
        fontSize: '0.9rem',
        margin: 0,
    },
    summaryTable: {
        width: '100%',
        maxWidth: 500,
        marginTop: '2rem',
        marginBottom: '2rem',
        borderCollapse: 'collapse',
        fontSize: '0.9rem',
        border: '1px solid #e0e0e0',
        borderRadius: 4,
    },
    summaryTableHeader: {
        textAlign: 'left',
        padding: '0.75rem 1rem',
        borderBottom: '2px solid #e0e0e0',
        borderRight: '1px solid #e0e0e0',
        fontWeight: 600,
        color: '#595652',
        '&:last-child': {
            borderRight: 'none',
        },
    },
    summaryTableCell: {
        textAlign: 'right',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid #e0e0e0',
        borderRight: '1px solid #e0e0e0',
        fontWeight: 600,
        '&:last-child': {
            borderRight: 'none',
        },
    },
    summaryTableLabel: {
        textAlign: 'left',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid #e0e0e0',
        borderRight: '1px solid #e0e0e0',
        color: '#333',
        '&:last-child': {
            borderRight: 'none',
        },
    },
    modalTextField: {
        marginTop: '1rem',
        width: '100%',
    },
};

const transformToChartData = dayCountMap => {
    if (!dayCountMap || Object.keys(dayCountMap).length === 0) {
        return [];
    }

    const sortedDays = Object.keys(dayCountMap).sort();

    return sortedDays.map(day => ({
        day,
        count: dayCountMap[day],
    }));
};

const formatDateInput = date => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const _Statistics = ({ classes }) => {
    const [stats, setStats] = useState(null);
    const [waterpipes, setWaterpipes] = useState([]);
    const [isServerError, setIsServerError] = useState(false);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [loading, setLoading] = useState(true);

    const [deviationSettings, setDeviationSettings] = useState({});
    const [deviationModal, setDeviationModal] = useState({
        open: false,
        chartKey: '',
        deviation_positive: '15',
        deviation_negative: '-15',
        days_back: '7',
    });

    const [daysBack, setDaysBack] = useState(7);
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return formatDateInput(d);
    });
    const [toDate, setToDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return formatDateInput(d);
    });
    const [selectedWaterpipes, setSelectedWaterpipes] = useState([]);
    const [maxDaysBack, setMaxDaysBack] = useState(30);
    const [committedDayCount, setCommittedDayCount] = useState(7);
    const [waterpipePanelOpen, setWaterpipePanelOpen] = useState(false);
    const waterpipePanelRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = e => {
            if (waterpipePanelRef.current && !waterpipePanelRef.current.contains(e.target)) {
                setWaterpipePanelOpen(false);
            }
        };

        if (waterpipePanelOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [waterpipePanelOpen]);

    // Refs to read current filter values on demand (no auto-refetch on change)
    const fromDateRef = useRef(fromDate);
    const toDateRef = useRef(toDate);
    const waterpipeRef = useRef(selectedWaterpipes);

    useEffect(() => {
        fromDateRef.current = fromDate;
        if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            const diff = Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
            if (diff >= 1 && diff <= maxDaysBack) {
                setDaysBack(diff);
            }
        }
    }, [fromDate, toDate, maxDaysBack]);
    useEffect(() => {
        toDateRef.current = toDate;
    }, [toDate]);
    useEffect(() => {
        waterpipeRef.current = selectedWaterpipes;
    }, [selectedWaterpipes]);

    const handleDaysBack = value => {
        const days = Math.max(1, Math.min(Number(value) || 1, maxDaysBack));
        setDaysBack(days);

        const now = new Date();
        const from = new Date(now);
        from.setDate(from.getDate() - days);
        setFromDate(formatDateInput(from));

        const to = new Date(now);
        to.setDate(to.getDate() - 1);
        setToDate(formatDateInput(to));
    };

    const deviationDaysRef = useRef(0);

    const fetchStats = useCallback(() => {
        const params = {};

        if (fromDateRef.current) {
            params.from = fromDateRef.current;
        }

        if (toDateRef.current) {
            params.to = toDateRef.current;
        }

        if (waterpipeRef.current.length > 0) {
            params.waterpipe_id = waterpipeRef.current;
        }

        if (deviationDaysRef.current > 0) {
            params.deviation_days = deviationDaysRef.current;
        }

        setIsServerError(false);
        setLoading(true);

        axiosInstance(endpoint.STATISTICS, {
            params,
            cancelToken: cancelHttpReq.token,
        })
            .then(content => {
                setStats(content?.data?.data);
                if (content?.data?.max_days_back) {
                    setMaxDaysBack(content.data.max_days_back);
                }

                if (fromDateRef.current && toDateRef.current) {
                    const from = new Date(fromDateRef.current);
                    const to = new Date(toDateRef.current);
                    const diff = Math.max(1, Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1);
                    setCommittedDayCount(diff);
                }

                setLoading(false);
            })
            .catch(err => {
                if (err?.response?.data?.message === 'Unauthenticated.') {
                    setIsUnauthorized(true);
                    setIsServerError(true);
                } else if (!axios.isCancel(err)) {
                    setIsServerError(true);
                }
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        axiosInstance(endpoint.WATERPIPES, {
            cancelToken: cancelHttpReq.token,
        })
            .then(content => {
                if (content?.data?.data) {
                    setWaterpipes(content.data.data);
                }
            })
            .catch(() => {
                // Silently fail — waterpipe filter just won't show options
            });
    }, []);

    useEffect(() => {
        axiosInstance(endpoint.CHART_DEVIATION_SETTINGS, {
            cancelToken: cancelHttpReq.token,
        })
            .then(content => {
                if (content?.data?.data) {
                    const settings = content.data.data;
                    setDeviationSettings(settings);

                    const maxDays = Object.values(settings).reduce(
                        (max, s) => Math.max(max, s.days_back || 0),
                        0
                    );
                    deviationDaysRef.current = maxDays;

                    fetchStats();
                }
            })
            .catch(() => {
                // Silently fail — defaults will be used
            });
    }, []);

    const clearWaterpipes = () => {
        setSelectedWaterpipes([]);
    };

    const toggleWaterpipe = id => {
        setSelectedWaterpipes(prev => (prev.includes(id) ? prev.filter(wp => wp !== id) : [...prev, id]));
    };

    const handleRefresh = () => {
        fetchStats();
    };

    const handleEditDeviation = chartKey => {
        const current = deviationSettings[chartKey] || {
            deviation_positive: 15,
            deviation_negative: -15,
            days_back: 7,
        };
        setDeviationModal({
            open: true,
            chartKey,
            deviation_positive: String(current.deviation_positive),
            deviation_negative: String(current.deviation_negative),
            days_back: String(current.days_back),
        });
    };

    const handleDeviationSave = () => {
        const { chartKey, deviation_positive, deviation_negative, days_back } = deviationModal;

        axiosInstance
            .put(`${endpoint.CHART_DEVIATION_SETTINGS}/${chartKey}`, {
                deviation_positive: Number(deviation_positive),
                deviation_negative: Number(deviation_negative),
                days_back: Number(days_back),
            })
            .then(() => {
                setDeviationSettings(prev => ({
                    ...prev,
                    [chartKey]: {
                        deviation_positive: Number(deviation_positive),
                        deviation_negative: Number(deviation_negative),
                        days_back: Number(days_back),
                    },
                }));
                setDeviationModal(prev => ({ ...prev, open: false }));
            })
            .catch(() => {
                // Silently fail
            });
    };

    const handleDeviationModalClose = () => {
        setDeviationModal(prev => ({ ...prev, open: false }));
    };

    if (isServerError) {
        return (
            <div className={classes.container}>
                <DisplayError isUnauthorized={isUnauthorized} />
            </div>
        );
    }

    const getHistoryData = (chartKey, innerKey) => {
        if (!stats?.deviation_history) return [];

        if (chartKey === 'total') {
            return transformToChartData(stats.deviation_history.total);
        }

        if (chartKey.startsWith('source-') && stats.deviation_history.source) {
            return transformToChartData(stats.deviation_history.source[innerKey]);
        }

        if (chartKey.startsWith('protocol-') && stats.deviation_history.protocol) {
            return transformToChartData(stats.deviation_history.protocol[innerKey]);
        }

        if (chartKey.startsWith('status-') && stats.deviation_history.status) {
            return transformToChartData(stats.deviation_history.status[innerKey]);
        }

        return [];
    };

    const buildChartItems = () => {
        const items = [];

        if (stats?.total) {
            const history = getHistoryData('total');
            items.push({
                key: 'total',
                data: transformToChartData(stats.total),
                combinedData: [...history, ...transformToChartData(stats.total)],
                dataKey: 'count',
                color: ITEM_COLORS.total,
                label: ITEM_LABELS.total,
                daysBack: deviationSettings?.total?.days_back || 7,
            });
        }

        if (stats?.source) {
            for (const key of Object.keys(stats.source)) {
                const chartKey = `source-${key}`;
                const history = getHistoryData(chartKey, key);
                items.push({
                    key: chartKey,
                    data: transformToChartData(stats.source[key]),
                    combinedData: [...history, ...transformToChartData(stats.source[key])],
                    dataKey: 'count',
                    color: ITEM_COLORS[key],
                    label: ITEM_LABELS[key] || key,
                    daysBack: deviationSettings?.[chartKey]?.days_back || 7,
                });
            }
        }

        if (stats?.protocol) {
            for (const key of Object.keys(stats.protocol)) {
                const chartKey = `protocol-${key}`;
                const history = getHistoryData(chartKey, key);
                items.push({
                    key: chartKey,
                    data: transformToChartData(stats.protocol[key]),
                    combinedData: [...history, ...transformToChartData(stats.protocol[key])],
                    dataKey: 'count',
                    color: ITEM_COLORS[key],
                    label: ITEM_LABELS[key] || key,
                    daysBack: deviationSettings?.[chartKey]?.days_back || 7,
                });
            }
        }

        if (stats?.status) {
            for (const key of Object.keys(stats.status)) {
                const chartKey = `status-${key}`;
                const history = getHistoryData(chartKey, key);
                items.push({
                    key: chartKey,
                    data: transformToChartData(stats.status[key]),
                    combinedData: [...history, ...transformToChartData(stats.status[key])],
                    dataKey: 'count',
                    color: ITEM_COLORS[key],
                    label: ITEM_LABELS[key] || key,
                    daysBack: deviationSettings?.[chartKey]?.days_back || 7,
                });
            }
        }

        return items;
    };

    const charts = buildChartItems();

    return (
        <div className={classes.container}>
            <div className={classes.filters}>
                <div className={classes.filterGroup}>
                    <label className={classes.filterLabel} htmlFor="filter-days-back">
                        Dni wstecz
                    </label>
                    <input
                        id="filter-days-back"
                        className={classes.filterInput}
                        type="number"
                        min={1}
                        max={maxDaysBack}
                        value={daysBack}
                        onChange={e => handleDaysBack(e.target.value)}
                        style={{ width: 65, minWidth: 65, height: 37, boxSizing: 'border-box' }}
                    />
                </div>

                <div className={classes.filterGroup}>
                    <label className={classes.filterLabel} htmlFor="filter-from">
                        Data od
                    </label>
                    <input
                        id="filter-from"
                        className={classes.filterInput}
                        type="date"
                        value={fromDate}
                        onChange={e => {
                            const val = e.target.value;

                            const maxFrom = new Date();
                            maxFrom.setDate(maxFrom.getDate() - maxDaysBack);
                            const maxFromStr = formatDateInput(maxFrom);
                            if (val < maxFromStr) {
                                setFromDate(maxFromStr);
                                return;
                            }

                            if (val > toDate) {
                                setFromDate(toDate);
                                return;
                            }

                            setFromDate(val);
                        }}
                        max={toDate || undefined}
                        min={
                            new Date(new Date().setDate(new Date().getDate() - maxDaysBack))
                                .toISOString().split('T')[0]
                        }
                    />
                </div>

                <div className={classes.filterGroup}>
                    <label className={classes.filterLabel} htmlFor="filter-to">
                        Data do
                    </label>
                    <input
                        id="filter-to"
                        className={classes.filterInput}
                        type="date"
                        value={toDate}
                        onChange={e => {
                            const val = e.target.value;

                            if (val < fromDate) {
                                setToDate(fromDate);
                                return;
                            }

                            const yesterday = new Date();
                            yesterday.setDate(yesterday.getDate() - 1);
                            const yesterdayStr = formatDateInput(yesterday);
                            if (val > yesterdayStr) {
                                setToDate(yesterdayStr);
                                return;
                            }
                            setToDate(val);
                        }}
                        max={
                            new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0]
                        }
                    />
                </div>

                <div className={classes.filterGroup}>
                    <label className={classes.filterLabel}>Wodociąg</label>
                    <div className={classes.checkboxPanelWrapper} ref={waterpipePanelRef}>
                        <div
                            className={classes.checkboxTrigger}
                            onClick={() => setWaterpipePanelOpen(prev => !prev)}>
                            <span>
                                {selectedWaterpipes.length === 0
                                    ? 'Wszystkie'
                                    : selectedWaterpipes.length === 1
                                      ? waterpipes.find(w => w.id === selectedWaterpipes[0])?.name
                                      : `${selectedWaterpipes.length} wybranych`}
                            </span>
                            <span style={{ fontSize: '0.7rem' }}>{waterpipePanelOpen ? '▲' : '▼'}</span>
                        </div>

                        {waterpipePanelOpen && (
                            <div className={classes.checkboxPanel}>
                                <div
                                    className={classes.checkboxRow}
                                    onClick={clearWaterpipes}
                                    style={{ borderBottom: '1px solid #e0e0e0' }}>
                                    <p className={classes.checkboxLabel}>Wyczyść</p>
                                </div>

                                {waterpipes.map(wp => (
                                    <div
                                        key={wp.id}
                                        className={classes.checkboxRow}
                                        onClick={() => toggleWaterpipe(wp.id)}>
                                        <input
                                            type="checkbox"
                                            checked={selectedWaterpipes.includes(wp.id)}
                                            readOnly
                                        />
                                        <p className={classes.checkboxLabel}>{wp.name}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <Button variant="contained" color="primary" onClick={handleRefresh} disabled={loading}>
                    {loading ? 'Ładowanie...' : 'Odśwież'}
                </Button>
            </div>

            {!loading && (
            <div
                className={`${classes.chartsGrid} ${committedDayCount > 15 ? classes.chartsGridSingle : classes.chartsGridDouble}`}>
                {charts.map(chart => (
                    <div key={chart.key} className={classes.chartWrapper}>
                        <div className={classes.chartHeader}>
                            <h3 className={classes.chartTitle}>{chart.label}</h3>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.editButton}
                                onClick={() => handleEditDeviation(chart.key)}>
                                <EditIcon style={{ fontSize: 14, marginRight: 4 }} />
                                Odchylenie
                            </Button>
                        </div>
                        <StatisticsChart
                            data={chart.data}
                            combinedData={chart.combinedData}
                            dataKey={chart.dataKey}
                            color={chart.color}
                            title=""
                            deviationSettings={deviationSettings[chart.key]}
                            daysBack={chart.daysBack}
                        />
                    </div>
                ))}
            </div>
            )}

            <Dialog
                open={deviationModal.open}
                onClose={handleDeviationModalClose}
                maxWidth="xs"
                fullWidth>
                <DialogTitle>Edytuj odchylenie</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Odchylenie dodatnie (%)"
                        type="text"
                        inputMode="decimal"
                        variant="filled"
                        value={deviationModal.deviation_positive}
                        onChange={e =>
                            setDeviationModal(prev => ({
                                ...prev,
                                deviation_positive: e.target.value
                                    .replace(/[^0-9.]/g, '')
                                    .replace(/(\..*)\./g, '$1'),
                            }))
                        }
                        className={classes.modalTextField}
                    />
                    <TextField
                        label="Odchylenie ujemne (%)"
                        type="text"
                        inputMode="decimal"
                        variant="filled"
                        value={deviationModal.deviation_negative}
                        onChange={e =>
                            setDeviationModal(prev => {
                                const digits = e.target.value
                                    .replace(/[^0-9.]/g, '')
                                    .replace(/(\..*)\./g, '$1');
                                return {
                                    ...prev,
                                    deviation_negative: digits ? `-${digits}` : '',
                                };
                            })
                        }
                        className={classes.modalTextField}
                    />
                    <TextField
                        label="Dni wstecz dla obliczenia średniej"
                        type="text"
                        inputMode="numeric"
                        variant="filled"
                        value={deviationModal.days_back}
                        onChange={e =>
                            setDeviationModal(prev => ({
                                ...prev,
                                days_back: e.target.value.replace(/\D/g, ''),
                            }))
                        }
                        className={classes.modalTextField}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeviationModalClose} color="default">
                        Anuluj
                    </Button>
                    <Button onClick={handleDeviationSave} color="primary" variant="contained">
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            {!loading && stats && (
            <table className={classes.summaryTable}>
                <thead>
                    <tr>
                        <th className={classes.summaryTableHeader}>Nazwa</th>
                        <th className={classes.summaryTableHeader}>Ilość</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={classes.summaryTableLabel}>Wodociągi</td>
                        <td className={classes.summaryTableCell}>{stats?.waterpipes ?? '-'}</td>
                    </tr>
                    <tr>
                        <td className={classes.summaryTableLabel}>Wszystkie pakiety</td>
                        <td className={classes.summaryTableCell}>{stats?.packages ?? '-'}</td>
                    </tr>
                </tbody>
            </table>
            )}
        </div>
    );
};

export default withStyles(styles)(_Statistics);
